import { createLazyService, createScope, createService } from '@tencent/tiny-ioc';
import type { IClientVarsService } from './interfaces/client-vars-service.interface';
import type { ISlideAppService } from './interfaces/slide-app-service.interface';
import type { ISlideInitialDataService } from './interfaces/slide-initial-data-service.interface';
import type { IContentStatusManager } from '@tencent/slide-sdk-model/src/manager/content-status-manager';
import { OperationLogger } from '@tencent/slide-sdk-devtools';
import { ISlideCountState } from '../store/slide-count-state';
import { ISlideTitleManager } from './interfaces/slide-title-manager.interface';
import {
    IColorManager,
    ICommandManager,
    IDataHelper,
    IEventManager,
    IMutationManager,
    ISearchManager,
} from '@tencent/slide-sdk-model';
import { IPresentationStoreService } from './interfaces/presentation-service.interface';
import { IPrivilegeManager } from './interfaces/privilege-manager.interface';
import { IDocInfoManager } from './interfaces/doc-info.interface';
import { IEditEvents } from './interfaces/event.interface';
import { ILogger } from './interfaces/logger.interface';
import { IUIEventManager } from './interfaces/ui-event-manager.interface';
import eventBus, { EventKeys } from '@src/slide/infrastructure/module/independent/event-emitter/instance';
import type { ISnapshotManager } from '@tencent/slide-sdk-persistent';
import { ISdkDataValidate } from '@tencent/slide-sdk-validate';
import type { ISlideFileManager } from './managers/slide-file-manager';
import type { ISyncManager } from './managers/sync-manager/sync-manager';

export type { ILogger };

export type IMqq = {
    invoke(serviceName: string, methodName: string, options?: object, callback?: (...args: any[]) => void): void;
};

export type ISnackbar = {
    show(options: {
        message: string,
        id?: string,
        type?: 'info' | 'success' | 'error' | 'loading',
        autoClose?: boolean,
        closable?: boolean,
        duration?: number,
        action?: string;
        onClickAction?: () => void;
        onClose?: () => void;
    })
};

export type IModal = {
    confirm(options: {
        title?: string,
        content?: string | JSX.Element,
        closable?: boolean,
        okText?: string,
        cancelText?: string | null,
        zIndex?: number,
        onOk?: () => void | false,
        onCancel?: () => void | false,
        onClose?: () => void | false,
    }): void
};

export const SlideDataServices = createScope(() => {
    const Infra = createScope(() => {
        const Logger = createService<ILogger>().dependsOn([]);
        const Mqq = createService<IMqq>().dependsOn([]);
        return { Logger, Mqq };
    });

    const UI = createScope(() => {
        const Snackbar = createLazyService<ISnackbar>().dependsOn([]);
        const Modal = createLazyService<IModal>().dependsOn([]);

        return {
            Snackbar,
            Modal,
        };
    });

    const States = createScope(() => {
        const SlideCountState = createService<ISlideCountState>().dependsOn([]);
        return { SlideCountState };
    });

    const OperationLogger = createService<OperationLogger>().dependsOn([]);
    const UIEventManager = createService<IUIEventManager>().dependsOn([]);
    const EventManager = createService<IEditEvents>().dependsOn([]);
    const ClientVarsService = createService<IClientVarsService>().dependsOn([Infra.Logger]);
    const DocInfoManager = createService<IDocInfoManager>().dependsOn([Infra.Logger, EventManager, ClientVarsService]);
    const SlideInitialDataService = createService<ISlideInitialDataService>().dependsOn([]);
    const ContentStatusManager = createService<IContentStatusManager>().dependsOn([]);
    const SDKDataServices = createScope(() => {
        const EventManager = createService<IEventManager>().dependsOn([]);
        const MutationManager = createService<IMutationManager>().dependsOn([
            EventManager,
            Infra.Logger,
        ]);
        const TransactionManager = createService<{}>().dependsOn([EventManager]);
        const ObserverManager = createService<{}>().dependsOn([EventManager]);
        const PresentationStoreService = createService<IPresentationStoreService>().dependsOn([
            SlideInitialDataService,
            ContentStatusManager,
            MutationManager,
            TransactionManager,
            EventManager,
            DocInfoManager,
            ObserverManager,
            Infra.Logger,
        ]);
        const SearchManager = createService<ISearchManager>().dependsOn([
            EventManager,
            ContentStatusManager,
        ]);
        const CommandManager = createService<ICommandManager>().dependsOn([
            EventManager,
            PresentationStoreService,
            Infra.Logger,
        ]);
        const ColorManager = createService<IColorManager>().dependsOn([]);
        const DataHelper = createService<IDataHelper>().dependsOn([]);
        const ResourceMapManager = createService<{}>().dependsOn([EventManager]);
        const SnapshotManager = createService<ISnapshotManager>().dependsOn([
            PresentationStoreService,
            ContentStatusManager,
            DocInfoManager,
            EventManager,
            Infra.Logger,
        ]);
        const DataValidator = createService<ISdkDataValidate>().dependsOn([]);

        return {
            PresentationStoreService,
            EventManager,
            TransactionManager,
            SearchManager,
            CommandManager,
            ObserverManager,
            ColorManager,
            DataHelper,
            ResourceMapManager,
            MutationManager,
            SnapshotManager,
            DataValidator,
        };
    });
    const PrivilegeManager = createService<IPrivilegeManager>().dependsOn([EventManager]);
    const SlideAppService = createService<ISlideAppService>().dependsOn([
        Infra.Logger,
        SDKDataServices.PresentationStoreService,
        ClientVarsService,
        ContentStatusManager,
    ]);
    const SlideTitleManager = createService<ISlideTitleManager>().dependsOn([
        ClientVarsService,
        Infra.Logger,
        SlideAppService,
    ]);
    const SyncManager = createService<ISyncManager>().dependsOn([
        DocInfoManager,
        SDKDataServices.PresentationStoreService,
        Infra.Logger,
    ]);
    const SlideFileManager = createService<ISlideFileManager>().dependsOn([]);

    return {
        Infra,
        UI,
        States,
        ClientVarsService,
        SlideInitialDataService,
        SlideAppService,
        ContentStatusManager,
        SlideTitleManager,
        SDKDataServices,
        PrivilegeManager,
        DocInfoManager,
        EventManager,
        UIEventManager,
        OperationLogger,
        SlideFileManager,
        SyncManager,
    };
});

/**
 * SlideApp.onReady
 */
export function onSlideAppReady(fn: () => void = () => {}) {
    SlideDataServices.SlideAppService.promise().then((slideAppService) => {
        slideAppService.onReady(fn);
    });
}

export function scheduleAfterFirstSlideDisplay<T>(task: () => Promise<T>): Promise<T> {
    return new Promise<T>((resolve) => {
        eventBus.onIncludingPast(EventKeys.FIRST_PAGE_DISPLAY, () => resolve(task()));
    });
}

export function scheduleAfterFirstScreenDisplay<T>(task: () => Promise<T>): Promise<T> {
    return new Promise<T>((resolve) => {
        eventBus.onIncludingPast(EventKeys.VISIBLE_PAGE_DISPLAY, () => resolve(task()));
    });
}
